import TenantProfileCard from "./TenantProfileCard";
import type { Profile } from "./types";
import Title from "../UI/Title";
import styles from "./TenantProfileList.module.css";
// @ts-expect-error pure js
import { classnames } from "../../utils";

interface TenantProfileListProps {
  profiles?: Profile[];
  profilePrimaryKey?: string;
  editProfileHandler?: (profileId: number, index: number) => void;
  addGarantProfileHandler?: (profileId: number, index: number) => void;
  addRepresentantProfileHandler?: (profileId: number, index: number) => void;
  deleteProfileHandler?: (profileId: number, index: number) => void;
  archiveProfileHandler?: (profileId: number, index: number) => void;
  activateProfileHandler?: (profileId: number, index: number) => void;
  inviteProfileHandler?: (profileId: number) => void | Promise<void>;
  revokeInviteProfileHandler?: (profileId: number) => void | Promise<void>;
  removeGarant?: (profileId: number) => void;
  removeRepresentant?: (profileId: number) => void;
  dontAddGarant?: boolean;
  isCandidat?: boolean;
  minimalDisplay?: boolean;
  title?: string;
  hideTitle?: boolean;
}

export default function TenantProfileList({
  profiles,
  profilePrimaryKey = "id",
  editProfileHandler,
  addGarantProfileHandler,
  addRepresentantProfileHandler,
  deleteProfileHandler,
  archiveProfileHandler,
  activateProfileHandler,
  inviteProfileHandler,
  revokeInviteProfileHandler,
  removeGarant,
  removeRepresentant,
  dontAddGarant,
  isCandidat,
  minimalDisplay,
  title = "Informations personnelles des locataires",
  hideTitle = false,
}: TenantProfileListProps) {
  const supprimerActionFactory = (profileId: number, index: number) => {
    if (!deleteProfileHandler) {
      return null;
    }
    return () => deleteProfileHandler(profileId, index);
  };
  const archiverActionFactory = (profileId: number, index: number) => {
    if (!archiveProfileHandler) {
      return null;
    }
    return () => archiveProfileHandler(profileId, index);
  };
  const activateActionFactory = (profileId: number, index: number) => {
    if (!activateProfileHandler) {
      return null;
    }
    return () => activateProfileHandler(profileId, index);
  };
  const modifierActionFactory = (index: number) => {
    if (!editProfileHandler) {
      return null;
    }
    return (profileId: number) => editProfileHandler(profileId, index);
  };
  const addGarantActionFactory = (index: number) => {
    if (!addGarantProfileHandler) {
      return null;
    }
    return (profileId: number) => addGarantProfileHandler(profileId, index);
  };
  const addRepresentantActionFactory = (index: number) => {
    if (!addRepresentantProfileHandler) {
      return null;
    }
    return (profileId: number) =>
      addRepresentantProfileHandler(profileId, index);
  };
  const inviteActionFactory = (profileId: number) => {
    if (!inviteProfileHandler) {
      return null;
    }
    return () => inviteProfileHandler(profileId);
  };
  const revokeInviteActionFactory = (profileId: number) => {
    if (!revokeInviteProfileHandler) {
      return null;
    }
    return () => revokeInviteProfileHandler(profileId);
  };

  const filteredProfiles = profiles?.filter(
    (p) => p.garantor_of === undefined || p.garantor_of === null,
  );

  if (!filteredProfiles || filteredProfiles.length === 0) return null;

  return (
    <div className={styles.container}>
      {!hideTitle && (
        <Title variant="deca" weight="bold" as="h2">
          {title}
        </Title>
      )}
      <div
        className={classnames(
          styles.grid,
          minimalDisplay && styles.gridMinimal,
        )}
      >
        {filteredProfiles.map((profile, index) => (
          <TenantProfileCard
            minimalDisplay={minimalDisplay}
            profile={profile}
            profilePrimaryKey={profilePrimaryKey}
            key={index}
            modifierAction={!isCandidat && modifierActionFactory(index)}
            addGarantAction={!isCandidat && addGarantActionFactory(index)}
            addRepresentantAction={
              !isCandidat && addRepresentantActionFactory(index)
            }
            supprimerAction={
              !isCandidat &&
              supprimerActionFactory(
                profile[profilePrimaryKey] as number,
                index,
              )
            }
            archiverAction={
              !isCandidat &&
              profiles &&
              profiles.length > 1 &&
              archiverActionFactory(profile.id as number, index)
            }
            activateAction={
              !isCandidat && activateActionFactory(profile.id as number, index)
            }
            inviteAction={
              !isCandidat && inviteActionFactory(profile.id as number)
            }
            revokeInviteAction={
              !isCandidat && revokeInviteActionFactory(profile.id as number)
            }
            removeGarant={!isCandidat && removeGarant}
            removeRepresentant={!isCandidat && removeRepresentant}
            dontAddGarant={dontAddGarant}
          />
        ))}
      </div>
    </div>
  );
}
